import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import schoolLogo from '../school-brand.jpg';

function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqData = [
    {
      q: "How do I sign up as a driving instructor?",
      a: "Click 'Register' in the top navigation or 'Get Started' on the homepage and create your instructor account. Once your account is set up, you can access your dashboard and begin adding and managing your own students."
    },
    {
      q: "Do you provide learners to instructors?",
      a: "We do not directly provide learners, but we offer our registered instructors great local exposure by featuring on our public homepage map, connecting you with potential students looking for lessons in your area."
    },
    {
      q: "How do I add a student?",
      a: "From your instructor dashboard, you can add your own students by entering their details, including their email address. Once added, the student will receive an invitation or account setup link so they can access their own Lesson-Lane account."
    },
    {
      q: "What happens when I add a student?",
      a: "When you add a student and provide their email address, Lesson-Lane can send them an invitation or account setup link. Once they activate their account, they can access the student features available to them and connect with your instructor account."
    },
    {
      q: "What can my students see?",
      a: "Students have their own account where they can see information relating to their lessons. They can view your available lesson times, book lessons, see upcoming sessions, view their lesson history and follow their learning progress."
    },
    {
      q: "Can my students book their own lessons?",
      a: "Yes. Once you have added a student and they have activated their account, they can view the lesson times you have made available and book a suitable slot. Once booked, the lesson is added to the shared schedule."
    },
    {
      q: "Can students see my live availability?",
      a: "Yes. Students can view the availability you make available through your instructor calendar. Booked or unavailable times are not shown as available booking slots."
    },
    {
      q: "What happens when a student books a lesson?",
      a: "The booking is added to the instructor's calendar and the student's upcoming lessons. This keeps both accounts up to date and helps prevent double bookings."
    },
    {
      q: "Can I control when students can book?",
      a: "Yes. Instructors manage their own availability through the dashboard. You control which times are available for students to book."
    },
    {
      q: "Can I message my students?",
      a: "Yes. Lesson-Lane includes messaging within the instructor dashboard, allowing you to communicate directly with the students you have added to your account. Students can view and respond to messages from their own account."
    },
    {
      q: "Can I track my students' progress?",
      a: "Yes. Lesson-Lane allows instructors to keep track of student progress and lesson information through the dashboard. Students can also view relevant progress information from their own account."
    },
    {
      q: "Does Lesson-Lane provide my students to other instructors?",
      a: "No. Your students are managed through your own instructor account. Lesson-Lane does not operate a shared student pool or allocate your students to other instructors."
    },
    {
      q: "Does Lesson-Lane have access to my student list?",
      a: "Lesson-Lane provides the platform and tools you use to manage your students, calendar, bookings, messages and progress. Student relationships remain managed through the individual instructor accounts and are not used to allocate students to other instructors."
    },
    {
      q: "What happens if I need to cancel or reschedule a lesson?",
      a: "Lesson arrangements are managed between the instructor and student. Your calendar allows you to manage lesson sessions and availability. Any cancellation or rescheduling should follow your own instructor policy and the arrangements agreed with your student."
    },
    {
      q: "Is Lesson-Lane suitable for manual and automatic instructors?",
      a: "Yes. Lesson-Lane is designed to support driving instructors who provide manual or automatic car training. The platform helps instructors manage their students, calendar, bookings, communication and progress."
    },
    {
      q: "What happens to my data if I delete my account?",
      a: "When an account is deleted, associated data will be permanently deleted in accordance with our data retention and deletion procedures. We recommend downloading or retaining any information you may need before deleting your account, as deleted information may not be recoverable."
    },
    {
      q: "Who is responsible for my students?",
      a: "The instructor remains responsible for their own students and teaching relationship. This includes lesson arrangements, communication, teaching and any professional or legal responsibilities that apply to the instructor's services."
    },
    {
      q: "Who is responsible for DVSA requirements and insurance?",
      a: "Independent driving instructors are responsible for maintaining any required DVSA approvals, badges, qualifications and appropriate insurance for the services they provide. Lesson-Lane is a management platform and does not replace these responsibilities."
    }
  ];

  return (
    <div
      style={{
        padding: '40px 16px',
        maxWidth: '850px',
        margin: '0 auto',
        color: '#111827',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        width: '100%',
        overflowX: 'hidden'
      }}
    >
      {/* Header with Logo Watermark */}
      <div 
        style={{ 
          textAlign: 'center', 
          marginBottom: '40px', 
          position: 'relative', 
          overflow: 'hidden', 
          padding: '20px 10px' 
        }}
      >
        {/* Subtle Watermark Logo Background */}
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '220px', height: '220px', backgroundImage: `url(${schoolLogo})`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', opacity: 0.10, pointerEvents: 'none', zIndex: 0 }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div
            style={{
              color: '#2563eb',
              fontSize: '0.85rem',
              fontWeight: '800',
              letterSpacing: '1px',
              marginBottom: '10px'
            }}
          >
            HELP & INFORMATION
          </div>

          <h1
            style={{
              fontSize: 'clamp(2rem, 5vw, 3.2rem)',
              fontWeight: '800',
              margin: '0 0 15px',
              letterSpacing: '-1px'
            }}
          >
            Frequently Asked Questions
          </h1>

          <p
            style={{
              color: '#64748b',
              fontSize: '1rem',
              lineHeight: '1.6',
              maxWidth: '650px',
              margin: '0 auto'
            }}
          >
            Everything you need to know about using Lesson-Lane to manage your
            students, lessons, calendar and communication.
          </p>
        </div>
      </div>

      {/* FAQ Accordion */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          marginBottom: '50px'
        }}
      >
        {faqData.map((item, idx) => {
          const isOpen = openIndex === idx;

          return (
            <div
              key={idx}
              onClick={() => toggleAccordion(idx)}
              style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '18px 20px',
                cursor: 'pointer',
                boxShadow: '0 4px 6px rgba(15, 23, 42, 0.02)',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '15px',
                  fontWeight: '700',
                  fontSize: '1rem',
                  color: '#111827'
                }}
              >
                <span>{item.q}</span>

                <span
                  style={{
                    color: '#2563eb',
                    fontSize: '1.2rem',
                    flexShrink: 0
                  }}
                >
                  {isOpen ? '−' : '+'}
                </span>
              </div>

              {isOpen && (
                <p
                  style={{
                    marginTop: '12px',
                    color: '#64748b',
                    lineHeight: '1.7',
                    fontSize: '0.92rem',
                    marginBottom: 0
                  }}
                >
                  {item.a}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* How Lesson-Lane Works */}
      <div
        style={{
          background: '#eff6ff',
          padding: '28px 20px',
          borderRadius: '20px',
          border: '1px solid #dbeafe',
          marginBottom: '35px',
          boxSizing: 'border-box'
        }}
      >
        <h2
          style={{
            fontSize: '1.35rem',
            fontWeight: '800',
            margin: '0 0 12px',
            color: '#1e3a8a'
          }}
        >
          How Lesson-Lane Works
        </h2>

        <p
          style={{
            color: '#475569',
            lineHeight: '1.7',
            fontSize: '0.92rem',
            marginBottom: '16px'
          }}
        >
          Lesson-Lane gives independent driving instructors the tools to
          manage their own students, lessons, calendar and communication in
          one place, alongside local map exposure for your practice.
        </p>

        <ul
          style={{
            color: '#475569',
            lineHeight: '1.7',
            fontSize: '0.92rem',
            paddingLeft: '20px',
            margin: 0
          }}
        >
          <li style={{ marginBottom: '8px' }}>
            <strong>Add your own students:</strong> Add the students you
            already teach to your instructor dashboard.
          </li>

          <li style={{ marginBottom: '8px' }}>
            <strong>Invite your student:</strong> Enter their email address
            and send them an invitation to access their account.
          </li>

          <li style={{ marginBottom: '8px' }}>
            <strong>Share availability:</strong> Your students can see the
            lesson times you make available through your calendar.
          </li>

          <li style={{ marginBottom: '8px' }}>
            <strong>Let students book:</strong> Students can choose an
            available lesson slot and book it directly.
          </li>

          <li style={{ marginBottom: '8px' }}>
            <strong>Stay synchronised:</strong> Bookings appear in the
            instructor and student accounts.
          </li>

          <li style={{ marginBottom: '8px' }}>
            <strong>Track progress:</strong> Manage and monitor student
            learning progress through the dashboard.
          </li>

          <li>
            <strong>Keep in touch:</strong> Message your students directly
            through Lesson-Lane.
          </li>
        </ul>
      </div>

      {/* Your Students & Your Account */}
      <div
        style={{
          background: '#ffffff',
          padding: '28px 20px',
          borderRadius: '20px',
          border: '1px solid #e2e8f0',
          marginBottom: '35px',
          boxSizing: 'border-box'
        }}
      >
        <h2
          style={{
            fontSize: '1.35rem',
            fontWeight: '800',
            margin: '0 0 12px',
            color: '#111827'
          }}
        >
          Your Students, Your Account
        </h2>

        <p
          style={{
            color: '#64748b',
            lineHeight: '1.7',
            fontSize: '0.92rem',
            margin: 0
          }}
        >
          Lesson-Lane is designed around instructors managing their own students. You decide which students to add, what availability to offer, and how you manage your lessons. Plus, we offer our registered instructors exposure on our homepage map to help link you with potential local students.
        </p>
      </div>

      {/* Beta Development & Future Roadmap */}
      <div
        style={{
          background: '#f0fdf4',
          padding: '28px 20px',
          borderRadius: '20px',
          border: '1px solid #bbf7d0',
          marginBottom: '35px',
          boxSizing: 'border-box'
        }}
      >
        <h2
          style={{
            fontSize: '1.35rem',
            fontWeight: '800',
            margin: '0 0 12px',
            color: '#166534'
          }}
        >
          Beta Development & Platform Perks
        </h2>

        <p
          style={{
            color: '#475569',
            lineHeight: '1.7',
            fontSize: '0.92rem',
            marginBottom: '12px'
          }}
        >
          Lesson-Lane is currently operating in an exclusive <b>beta development phase</b>. Our primary focus is refining the platform for our active beta testers. 
        </p>

        <p
          style={{
            color: '#475569',
            lineHeight: '1.7',
            fontSize: '0.92rem',
            margin: 0
          }}
        >
          While subscription models and pricing structures are planned for future public rollout, final pricing terms have not yet been determined. Appropriate notice will be provided to active users ahead of any transition out of the beta testing phase.
        </p>
      </div>

      {/* Terms & Conditions */}
      <div
        style={{
          background: '#f8fafc',
          padding: '28px 20px',
          borderRadius: '20px',
          border: '1px solid #e2e8f0',
          marginBottom: '35px',
          boxSizing: 'border-box'
        }}
      >
        <h2
          style={{
            fontSize: '1.35rem',
            fontWeight: '800',
            margin: '0 0 12px',
            color: '#111827'
          }}
        >
          Terms & Conditions
        </h2>

        <p
          style={{
            color: '#64748b',
            lineHeight: '1.7',
            fontSize: '0.92rem',
            marginBottom: '15px'
          }}
        >
          Welcome to Lesson-Lane. By accessing or using our platform, you
          agree to comply with the applicable terms and conditions governing
          use of the service.
        </p>

        <ul
          style={{
            color: '#64748b',
            lineHeight: '1.7',
            fontSize: '0.92rem',
            paddingLeft: '20px',
            margin: 0
          }}
        >
          <li style={{ marginBottom: '8px' }}>
            <strong>Account Accuracy:</strong> Users must provide accurate,
            current and complete information when creating and maintaining
            their account. We reserve the right to suspend or terminate accounts that violate our policies or platform integrity.
          </li>

          <li style={{ marginBottom: '8px' }}>
            <strong>Platform & Pricing Terms:</strong> Lesson-Lane is currently under active beta development. We reserve the right to introduce paid plans or subscriptions in the future with prior notice to users.
          </li>

          <li style={{ marginBottom: '8px' }}>
            <strong>Instructor Responsibility:</strong> Instructors are
            responsible for their own students, teaching arrangements,
            qualifications, DVSA requirements, badges and appropriate
            insurance.
          </li>

          <li style={{ marginBottom: '8px' }}>
            <strong>Student Management:</strong> Instructors are responsible
            for adding and managing their own students. While we offer map exposure to help connect potential students with registered instructors, instructors maintain their own independent teaching relationships.
          </li>

          <li style={{ marginBottom: '8px' }}>
            <strong>Calendar & Bookings:</strong> Instructors manage their own
            availability and lesson arrangements. Students can book available
            slots made available by their instructor.
          </li>

          <li style={{ marginBottom: '8px' }}>
            <strong>Messaging:</strong> Instructors and their students may use
            the platform's messaging features to communicate about lessons
            and related matters.
          </li>

          <li style={{ marginBottom: '8px' }}>
            <strong>Progress Information:</strong> Instructors are responsible
            for the accuracy of progress information and records they enter
            into the platform.
          </li>

          <li style={{ marginBottom: '8px' }}>
            <strong>Account Deletion:</strong> When an account is deleted,
            associated data may be permanently deleted in accordance with our
            data retention and deletion procedures.
          </li>

          <li>
            <strong>Data Privacy:</strong> Personal information is handled in
            accordance with applicable UK data protection requirements and the
            Lesson-Lane Privacy Policy.
          </li>
        </ul>
      </div>

      {/* Call to Action */}
      <div
        style={{
          textAlign: 'center',
          background: '#eef6ff',
          padding: '35px 20px',
          borderRadius: '20px',
          boxSizing: 'border-box'
        }}
      >
        <h3
          style={{
            fontSize: '1.25rem',
            fontWeight: '800',
            margin: '0 0 10px',
            color: '#1e3a8a'
          }}
        >
          Still have questions?
        </h3>

        <p
          style={{
            color: '#475569',
            marginBottom: '20px',
            fontSize: '0.92rem'
          }}
        >
          Our support team is always here to help you get started.
        </p>

        <Link to="/contact">
          <button
            style={{
              padding: '12px 25px',
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              fontWeight: '700',
              cursor: 'pointer',
              fontSize: '0.92rem'
            }}
          >
            Contact Support 💬
          </button>
        </Link>
      </div>
    </div>
  );
}

export default FAQ;